// @ts-nocheck
import { useState, useEffect, useRef, useCallback } from "react";
import { Users, Target, Sparkles, AlertCircle, Mail, Eye, Activity, TrendingUp } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import ABTestingDemo from "./components/templates/ABTestingDemo";
import CreateSegmentModal from "./components/segments/CreateSegmentModal";
import EnhancedAudiencesView from "./components/segments/EnhancedAudiencesView";
import EnhancedLeadsView from "./components/leads/EnhancedLeadsView";
import EnhancedCampaignsView from "./components/campaigns/EnhancedCampaignsView";
import PaymentsView from "./components/support/PaymentsView";
import { TemplateProvider } from "./contexts/TemplateContext";
import { NavigationProvider } from "./contexts/NavigationContext";
import { HeaderProvider, useHeader } from "./contexts/HeaderContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import TemplateGallery from "./components/templates/TemplateGallery";
import TemplatesHeader from "./components/templates/TemplatesHeader";
import { TemplateCardGridSkeleton } from "./skeleton/template/TemplateSkeleton";
import { useGlobalTemplates, useUserTemplates } from 'core';
import { PageContainer } from './components/ui/page-container';
import ABTestingPage from "./components/campaigns/ABTestingPage";
// AI Agent Components
import { AIAgentDashboard } from "./components/templates/abtest/AIAgentDashboard";
import AgentCollaborationDashboard from "./components/templates/abtest/AgentCollaborationDashboard";
import { AIAgentDecisionExplainer } from "./components/templates/abtest/AIAgentDecisionExplainer";
import { AgentReasoningTrail } from "./components/templates/abtest/AgentReasoningTrail";
import { AIAgentImpactTracker } from "./components/templates/abtest/AIAgentImpactTracker";
import { AgentLearningProgression } from "./components/templates/abtest/AgentLearningProgression";
import LiveMonitoringDemo from "./components/templates/abtest/LiveMonitoringDemo";
import EnhancedDashboard from "./components/dashboard/EnhancedDashboard";

import APIIntegrationsView from "./components/api/APIIntegrationsView";
import { IntegrationsCallbackRoute } from "new-components/pages/integrations/IntegrationsRoute";
import { INTEGRATIONS_CALLBACK_PATH } from "core/features/integrations/config";
import AppShell from "./new-components/layout/AppShell";
import { toast } from "components/ui/toast";
import { useConfirm } from "components/ui/confirm";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogBody,
    DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Field, FormGrid } from "components/ui/field";
import { IconTile } from "components/ui/icon-tile";
import { Page } from "components/ui/page";
import PageHeader from "components/ui/PageHeader";
import { Routes, Route, Navigate } from "react-router-dom";
import TemplateEditor from "components/templates/template-editor/TemplateEditor";
import LeadsForm from "components/leads/leads-form/LeadsForm";
import LeadsGroupForm from "components/leads/leads-form/LeadsGroupForm";
import AiAgent from "components/ai-agents/AiAgent";
import CampaignForm from "components/campaigns/campaign-form/CampaignForm";
import IdentityComponent from "components/identity/Identity";
import Launch from "components/launch/Launch";
import Widgets from "components/widgets/Widgets";
import Trash from "components/trash/Trash";
import FeedbackView from "components/feedback/FeedbackView";
import HelpView from "components/help/HelpView";
import DocumentView from "components/document/DocumentView";
import HistoryView from "components/history/HistoryView";
import SupportDashboard from "components/support/SupportDashboard";
import AIPlayground from "components/ai-playground/AIPlayground";
import DeveloperSetting from "components/developer/DeveloperSetting";
import UserProfile from "components/profile/Profile";
import DrawflowComponent from "components/campaigns/draw-flow/Drawflow";
import ContentBuilderPage from "core/features/content-builder/ContentBuilderPage";
import TemplateListPage from "core/features/content-builder/TemplateListPage";
import {
    useProfileDetails,
    createCampaignsRes,
    createABCampaignRes,
    abCampaignErrorMessage,
    campaignStatusByName,
} from "core";
import { queryClient } from "./core/query/queryClient";
import ReactivationPage from "./components/account/ReactivationPage";
import { SelectField } from "components/ui/select-field";

// ******************************** Email Marketing Dashboard Component ******************************** //
export const EmailMarketingDashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [activeTab, setActiveTab] = useState("dashboard");
    const { updateView } = useHeader();
    const [showAIAssistant, setShowAIAssistant] = useState(false);
    const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
    const [showCreateSegmentModal, setShowCreateSegmentModal] = useState(false);
    const [editingSegment, setEditingSegment] = useState(null);
    const [segmentFilterFromAudiences, setSegmentFilterFromAudiences] = useState(null);
    /* ── Notifications ──────────────────────────────────────────────────
       This file used to render its own green banner pinned to the top-right,
       while Layout.tsx rendered a dark pill bottom-right and a Radix Toaster
       was already mounted globally — three notification systems, so the same
       action could surface in a different place depending on the code path
       that produced it.

       Everything now goes through the one global Toaster. The two setters are
       kept as shims because ~20 call sites below use them, and they are called
       in both orders (message-then-show, and show-then-message); driving the
       toast from `setSuccessMessage` — which always receives the final text —
       means neither ordering can produce a stale or empty notification. */
    const showSuccessNotification = false;
    const setShowSuccessNotification = useCallback((_show) => {
        /* superseded by the global Toaster */
    }, []);
    const setSuccessMessage = useCallback((message) => {
        if (!message) return;
        const isError = /^error[:\s]/i.test(message);
        if (isError) {
            toast.error({ title: message.replace(/^error[:\s]*/i, "").trim() || message });
        } else {
            toast.success({ title: message });
        }
    }, []);
    const successMessage = "";
    const [segments, setSegments] = useState([]);
    const [newCampaign, setNewCampaign] = useState({
        name: "",
        type: "promotional",
        template: "",
        audience: "",
        segmentId: "",
        estimatedReach: 0,
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [segmentSuggestions, setSegmentSuggestions] = useState([]);

    /** Themed, promise-based replacement for window.confirm(). */
    const confirm = useConfirm();

    const isTouchDevice = typeof window !== "undefined" &&
        ("ontouchstart" in window || navigator.maxTouchPoints > 0);

    useEffect(() => {
        if (typeof window !== "undefined") {
            const isAndroidDevice = /Android/i.test(navigator.userAgent);
            if (isAndroidDevice) {
                document.documentElement.classList.add("is-android");
            } else {
                document.documentElement.classList.remove("is-android");
            }
        }
    }, []);

    const handleSegmentChange = (segmentId) => {
        const selectedSegment = segments.find((s) => s.id === segmentId);
        const estimatedReach = selectedSegment ? selectedSegment.size : 0;

        setNewCampaign({
            ...newCampaign,
            segmentId,
            audience: selectedSegment ? selectedSegment.name : "",
            estimatedReach,
        });

        // Clear validation errors when segment is selected
        if (validationErrors.segmentSize) {
            setValidationErrors({ ...validationErrors, segmentSize: null });
        }

        // Generate segment suggestions based on campaign type
        generateSegmentSuggestions(newCampaign.type, selectedSegment);
    };

    // Generate segment-based campaign suggestions
    const generateSegmentSuggestions = (campaignType, selectedSegment) => {
        if (!selectedSegment) return;

        const suggestions = [];

        // Suggest based on segment performance and campaign type
        if (
            campaignType === "promotional" &&
            selectedSegment.expectedPerformance?.conversionRate > 0.04
        ) {
            suggestions.push(
                `This segment has a high conversion rate (${(selectedSegment.expectedPerformance.conversionRate * 100).toFixed(1)}%) - perfect for promotional campaigns!`,
            );
        }

        if (
            selectedSegment.tags?.includes("inactive") &&
            campaignType !== "automated"
        ) {
            suggestions.push(
                "Consider using an automated re-engagement campaign for inactive users.",
            );
        }

        if (selectedSegment.size > 5000 && campaignType === "promotional") {
            suggestions.push(
                "Large audience detected - consider A/B testing different subject lines for optimal results.",
            );
        }

        setSegmentSuggestions(suggestions);
    };

    // Validate segment selection for statistical significance
    const validateSegmentSize = (segmentId) => {
        const selectedSegment = segments.find((s) => s.id === segmentId);
        const minSize = 100; // Minimum size for statistical significance

        if (selectedSegment && selectedSegment.size < minSize) {
            return `Segment size (${selectedSegment.size}) is below recommended minimum (${minSize}) for reliable results.`;
        }
        return null;
    };

    // Handle campaign creation with validation
    const handleCreateCampaign = () => {
        const errors = {};

        // Validate required fields
        if (!newCampaign.name.trim()) {
            errors.name = "Campaign name is required";
        }

        if (!newCampaign.segmentId) {
            errors.segment = "Please select an audience segment";
        } else {
            const sizeError = validateSegmentSize(newCampaign.segmentId);
            if (sizeError) {
                errors.segmentSize = sizeError;
            }
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Track segment usage
        const selectedSegment = segments.find(
            (s) => s.id === newCampaign.segmentId,
        );
        if (selectedSegment) {
            const updatedSegments = segments.map((segment) =>
                segment.id === newCampaign.segmentId
                    ? {
                        ...segment,
                        usageCount: (segment.usageCount || 0) + 1,
                        lastUsed: new Date(),
                    }
                    : segment,
            );
            setSegments(updatedSegments);
        }

        // Create campaign (in real app, this would make an API call)
        console.log("Creating campaign:", newCampaign);

        // Show success message
        setSuccessMessage(
            `Campaign "${newCampaign.name}" created successfully with ${newCampaign.estimatedReach.toLocaleString()} targeted leads!`,
        );
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 5000);

        // Reset form and close modal
        setShowNewCampaignModal(false);
        setNewCampaign({
            name: "",
            type: "promotional",
            template: "",
            audience: "",
            segmentId: "",
            estimatedReach: 0,
        });
        setValidationErrors({});
        setSegmentSuggestions([]);
    };

    const [campaigns, setCampaigns] = useState([]);

    const templates = [];

    const [leads, setLeads] = useState([]);

    /* Creates the campaign. This used to console.log the form and announce
       "Campaign created successfully!" without calling anything — the campaign
       was gone on refresh, and the user had been told it worked. It now posts
       the same payload CampaignForm sends, then invalidates the campaign
       queries so the list the user is looking at actually updates.

       It rethrows on failure: CreateCampaignModal awaits this call and shows
       its own submit error, and swallowing the rejection here is what would
       reintroduce a success message for work that did not happen. */
    const handleCreateCampaignFromView = async (campaignData) => {
        const body = campaignData?.api;
        if (!body) throw new Error("Campaign form produced no request payload");

        /* An A/B campaign is a different endpoint with a different body, so the
           form says which one it built rather than this guessing from fields. */
        if (campaignData.abTest) {
            try {
                await createABCampaignRes(body);
            } catch (error) {
                /* This endpoint answers a rejected field with a bare JSON
                   string, so `error.message` is the transport's. Rethrow the
                   server's own words — the modal shows what it is given. */
                const said = abCampaignErrorMessage(error);
                throw said ? new Error(said) : error;
            }
        } else {
            await createCampaignsRes(body);
        }

        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
            queryClient.invalidateQueries({ queryKey: ["historyCampaigns"] }),
        ]);

        /* A save made with too few credits is still a 200: the campaign is
           stored as PAUSED and nothing is deducted, with nothing in the
           response to say so. Reporting "created" and leaving it there is how
           a campaign silently never sends, so the status is read back. */
        const savedStatus = campaignData.abTest ? await campaignStatusByName(body.name) : null;
        const paused = String(savedStatus ?? "").toUpperCase() === "PAUSED";

        setShowSuccessNotification(true);
        if (paused) {
            setSuccessMessage(
                `A/B campaign "${body.name}" was saved but is paused — there are not enough credits for the whole audience. Top up, then resume it from Campaigns.`,
            );
        } else if (campaignData.abTest) {
            setSuccessMessage(
                `A/B campaign "${body.name}" created. Variant A and B go to ${body.testPercentage}% of the audience; the winner follows.`,
            );
        } else {
            setSuccessMessage(`Campaign "${body.name}" created.`);
        }
        setTimeout(() => setShowSuccessNotification(false), paused ? 10000 : 6000);
    };

    const handleEditCampaign = (updatedCampaign) => {
        console.log("Updating campaign:", updatedCampaign);

        // Update the campaign in the campaigns array
        setCampaigns((prevCampaigns) =>
            prevCampaigns.map((campaign) =>
                campaign.id === updatedCampaign.id ? updatedCampaign : campaign,
            ),
        );

        // Show success notification
        setSuccessMessage("Campaign updated successfully!");
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 4000);
    };

    const handleDeleteCampaign = (campaignId) => {
        console.log("Deleting campaign:", campaignId);
        // Show success notification - actual deletion is handled by mutation in EnhancedCampaignsView
        setShowSuccessNotification(true);
        setSuccessMessage("Campaign deleted successfully!");
        setTimeout(() => setShowSuccessNotification(false), 4000);
    };

    const handleDuplicateCampaign = (campaign) => {
        console.log("Duplicating campaign:", campaign);
        // In a real app, this would create a copy of the campaign
    };

    const handlePauseCampaign = (campaignId) => {
        console.log("Pausing campaign:", campaignId);
        // In a real app, this would update the campaign status
    };

    const handleResumeCampaign = (campaignId) => {
        console.log("Resuming campaign:", campaignId);
        // In a real app, this would update the campaign status
    };

    const handleViewCampaignDetails = (campaign) => {
        console.log("Viewing campaign details:", campaign);
        // In a real app, this would open a details modal or navigate to details page
    };

    const handleConfigureAI = (campaignId, agentId) => {
        console.log(
            "Configuring AI for campaign:",
            campaignId,
            "with agent:",
            agentId,
        );
        // In a real app, this would configure AI agent for the campaign
    };

    useEffect(() => {
        updateView(activeTab);
        window.currentActiveTab = activeTab;
        window.abTestingCreateTestRef = abTestingCreateTestRef;
    }, [activeTab, updateView]);

    const handleRoleSwitch = (newRole) => {
        console.log("Role switched to:", newRole);
    };

    const handleNotificationClick = (notification) => {
        console.log("Notification clicked:", notification);
    };

    const handleShowProfile = () => {
        console.log("Show profile");
    };

    const handleShowSettings = () => {
        console.log("Show settings");
        setActiveTab("settings");
    };

    const handleLogout = () => {
        console.log("Logout");
    };

    const handleCustomAction = (actionId) => {
        console.log("Custom action triggered:", actionId);

        switch (actionId) {
            case "new-test":
                console.log("Creating new A/B test");
                break;
            case "create-segment":
                setShowCreateSegmentModal(true);
                break;
            case "add-lead":
                setShowAddLeadModal(true);
                break;
            case "new-template":
                console.log("Creating new template");
                break;
            case "new-ticket":
                console.log("Creating new support ticket");
                break;
            default:
                console.log("Unknown custom action:", actionId);
        }
    };

    const abTestingCreateTestRef = useRef(null);

    /* Scroll containment for iOS now lives in AppShell, alongside the
       scroll container it guards. */

    const handleShowNewCampaign = () => {
        console.log("handleShowNewCampaign called, activeTab:", activeTab);
        console.log(
            "abTestingCreateTestRef.current:",
            abTestingCreateTestRef.current,
        );

        if (activeTab === "ab-testing" && abTestingCreateTestRef.current) {
            console.log("Opening ABTestManager modal");
            abTestingCreateTestRef.current();
        } else {
            console.log("Opening new campaign modal");
            setShowNewCampaignModal(true);
        }
    };

    const TemplatesView = () => {
        return (
            <TemplateProvider>
                <TemplateViewContainer />
            </TemplateProvider>
        );
    };

    const TemplateViewContainer = () => {
        const navigate = useNavigate();
        const { data: globalTemplates, isLoading: loadingGlobal } = useGlobalTemplates();
        const { data: userTemplates, isLoading: loadingUser } = useUserTemplates({ paginator: 0 });

        // Check if still loading
        const isLoading = loadingGlobal || loadingUser;

        // Calculate KPI metrics
        const totalTemplates = (globalTemplates?.length || 0) + (userTemplates?.items?.length || 0);
        const userTemplatesCount = userTemplates?.items?.length || 0;
        const globalTemplatesCount = globalTemplates?.length || 0;

        // Calculate average usage from user templates
        const averageUsage = userTemplatesCount > 0
            ? Math.round(
                userTemplates.items.reduce((sum, t) => sum + (t.metadata?.usage || 0), 0) / userTemplatesCount
            )
            : 0;

        const handleCreateTemplate = () => {
            navigate('/templates/editor/new');
        };

        const handleImportTemplate = () => {
            console.log('Import template clicked');
            // TODO: Implement import functionality
        };

        return (
            <PageContainer>
                {/* Always show header - only KPI values show skeleton when loading */}
                <TemplatesHeader
                    totalTemplates={totalTemplates}
                    userTemplatesCount={userTemplatesCount}
                    globalTemplatesCount={globalTemplatesCount}
                    averageUsage={averageUsage}
                    onCreateTemplate={handleCreateTemplate}
                    onImportTemplate={handleImportTemplate}
                    isLoading={isLoading}
                />
                {/* Show template grid skeleton or actual templates */}
                {isLoading ? (
                    <TemplateCardGridSkeleton count={12} />
                ) : (
                    <TemplateGallery />
                )}
            </PageContainer>
        );
    };

    const closeNewCampaignModal = () => {
        setShowNewCampaignModal(false);
        setValidationErrors({});
        setSegmentSuggestions([]);
    };

    const selectedCampaignSegment = segments.find((s) => s.id === newCampaign.segmentId);

    /* ══════════════════════════════════════════════════════════════════
       Create Campaign (quick create)

       Same rebuild as Add Lead. The estimated-reach and AI-suggestion panels
       were the useful part of this form and are kept — but promoted: reach is
       now the primary readout of the audience step rather than a coloured box
       appended below it, so the consequence of choosing a segment is visible
       at the moment of choosing.

       All validation, `handleSegmentChange`, `generateSegmentSuggestions` and
       `handleCreateCampaign` behaviour is unchanged.
       ══════════════════════════════════════════════════════════════════ */
    const newCampaignModalJSX = (
        <Dialog open={showNewCampaignModal} onOpenChange={(open) => !open && closeNewCampaignModal()}>
            <DialogContent size="lg" padding={false}>
                <DialogHeader className="px-5 pt-5 pb-4 border-b border-line pr-12">
                    <DialogTitle>Create campaign</DialogTitle>
                    <DialogDescription>
                        Pick an audience and a template — you can refine the content afterwards.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="px-5 pt-4 space-y-4">
                    <Field label="Campaign name" required error={validationErrors.name}>
                        {(control) => (
                            <Input
                                {...control}
                                value={newCampaign.name}
                                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                                placeholder="Spring product launch"
                            />
                        )}
                    </Field>

                    <FormGrid>
                        <Field label="Campaign type">
                            {(control) => (
                                <SelectField
                                    {...control}
                                    value={newCampaign.type}
                                    onChange={(e) => {
                                        setNewCampaign({ ...newCampaign, type: e.target.value });
                                        if (newCampaign.segmentId) {
                                            generateSegmentSuggestions(
                                                e.target.value,
                                                segments.find((s) => s.id === newCampaign.segmentId),
                                            );
                                        }
                                    }}
                                    className="input-field text-ui font-medium"
                                >
                                    <option value="promotional">Promotional</option>
                                    <option value="automated">Automated</option>
                                    <option value="announcement">Announcement</option>
                                </SelectField>
                            )}
                        </Field>

                        <Field label="Template">
                            {(control) => (
                                <SelectField
                                    {...control}
                                    value={newCampaign.template}
                                    onChange={(e) =>
                                        setNewCampaign({ ...newCampaign, template: e.target.value })
                                    }
                                    className="input-field text-ui font-medium"
                                >
                                    <option value="">Select a template</option>
                                    {templates.map((template) => (
                                        <option key={template.id} value={template.id}>
                                            {template.name}
                                        </option>
                                    ))}
                                </SelectField>
                            )}
                        </Field>
                    </FormGrid>

                    <Field
                        label="Audience segment"
                        required
                        error={validationErrors.segment}
                        hint="Segments are ordered by how often you've used them."
                    >
                        {(control) => (
                            <SelectField
                                {...control}
                                value={newCampaign.segmentId}
                                onChange={(e) => handleSegmentChange(e.target.value)}
                                className="input-field text-ui font-medium"
                            >
                                <option value="">Select an audience segment</option>
                                {segments
                                    .filter((segment) => segment.status === "active")
                                    .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
                                    .map((segment) => (
                                        <option key={segment.id} value={segment.id}>
                                            {segment.name} ({segment.size.toLocaleString()} leads)
                                            {segment.usageCount > 0 && ` • Used ${segment.usageCount} times`}
                                        </option>
                                    ))}
                            </SelectField>
                        )}
                    </Field>

                    {/* Minimum-audience rule — a business constraint, so it is
                        stated as a warning rather than a field-level error. */}
                    {validationErrors.segmentSize && (
                        <div
                            role="alert"
                            className="flex items-start gap-2 p-3 rounded-xl bg-warning-subtle border border-warning-border text-ui text-warning-on-subtle"
                        >
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                            <span>{validationErrors.segmentSize}</span>
                        </div>
                    )}

                    {/* Estimated reach — the consequence of the audience choice */}
                    {newCampaign.estimatedReach > 0 && (
                        <div className="rounded-xl bg-brand-subtle border border-brand-border p-4">
                            <div className="flex items-center gap-2 mb-1.5">
                                <Target className="w-4 h-4 text-brand" aria-hidden="true" />
                                <span className="text-meta font-semibold text-brand-on-subtle">
                                    Estimated reach
                                </span>
                            </div>
                            <p className="text-display-sm font-bold text-brand-on-subtle tabular-nums leading-none">
                                {newCampaign.estimatedReach.toLocaleString()}
                                <span className="text-ui font-semibold ml-1.5">leads</span>
                            </p>
                            {selectedCampaignSegment?.expectedPerformance && (
                                <p className="mt-2 text-meta text-brand-on-subtle/80 tabular-nums">
                                    Expected ~
                                    {Math.round(
                                        newCampaign.estimatedReach *
                                        selectedCampaignSegment.expectedPerformance.openRate,
                                    ).toLocaleString()}{" "}
                                    opens and ~
                                    {Math.round(
                                        newCampaign.estimatedReach *
                                        selectedCampaignSegment.expectedPerformance.clickRate,
                                    ).toLocaleString()}{" "}
                                    clicks
                                </p>
                            )}
                        </div>
                    )}

                    {segmentSuggestions.length > 0 && (
                        <div className="rounded-xl bg-violet-subtle border border-violet-border p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-4 h-4 text-violet" aria-hidden="true" />
                                <span className="text-meta font-semibold text-violet-on-subtle">
                                    AI suggestions
                                </span>
                            </div>
                            <ul className="space-y-1.5">
                                {segmentSuggestions.map((suggestion, index) => (
                                    <li
                                        key={index}
                                        className="text-ui text-violet-on-subtle flex items-start gap-2 leading-relaxed"
                                    >
                                        <span
                                            className="w-1 h-1 rounded-full bg-violet mt-2 flex-shrink-0"
                                            aria-hidden="true"
                                        />
                                        {suggestion}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </DialogBody>

                <DialogFooter className="px-5 pb-5 pt-0">
                    <Button variant="secondary" onClick={closeNewCampaignModal}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateCampaign}
                        disabled={!newCampaign.name.trim() || !newCampaign.segmentId}
                    >
                        Create campaign
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    /* ══════════════════════════════════════════════════════════════════
       AI Assistant launcher

       Every control in the previous version was inert: three action cards
       with no onClick, and a text input with no state, no submit and no
       handler — it looked interactive and did nothing. The panel also had no
       background class, so it rendered transparent over the scrim.

       The app already ships a working assistant at /ai-playground, so this is
       now a launcher into it: each shortcut goes to the real screen that does
       the job, and anything typed here is carried into the assistant's
       composer rather than discarded.
       ══════════════════════════════════════════════════════════════════ */
    const AIAssistant = () => {
        const [assistantPrompt, setAssistantPrompt] = useState("");

        const closeAssistant = () => {
            setShowAIAssistant(false);
            setAssistantPrompt("");
        };

        const goTo = (path, state) => {
            closeAssistant();
            navigate(path, state ? { state } : undefined);
        };

        const shortcuts = [
            {
                icon: Mail,
                tone: "brand",
                title: "Draft a campaign",
                description: "Generate subject lines and body copy with the assistant",
                onSelect: () =>
                    goTo("/ai-playground", { prompt: "Draft an email campaign for " }),
            },
            {
                icon: Target,
                tone: "success",
                title: "Improve an existing campaign",
                description: "Review performance and get concrete suggestions",
                onSelect: () => goTo("/campaigns"),
            },
            {
                icon: Users,
                tone: "violet",
                title: "Build an audience segment",
                description: "Describe who you want to reach and refine the criteria",
                onSelect: () => {
                    closeAssistant();
                    setShowCreateSegmentModal(true);
                },
            },
        ];

        const submitPrompt = (event) => {
            event.preventDefault();
            const prompt = assistantPrompt.trim();
            goTo("/ai-playground", prompt ? { prompt } : undefined);
        };

        return (
            <Dialog open={showAIAssistant} onOpenChange={(open) => !open && closeAssistant()}>
                <DialogContent size="md" padding={false}>
                    <DialogHeader className="flex items-start gap-3 px-5 pt-5 pb-4 border-b border-line pr-12">
                        <span
                            className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[image:var(--brand-gradient)] text-white"
                            aria-hidden="true"
                        >
                            <Sparkles className="w-5 h-5" />
                        </span>
                        <span className="min-w-0">
                            <DialogTitle>AI Assistant</DialogTitle>
                            <DialogDescription>What would you like to work on?</DialogDescription>
                        </span>
                    </DialogHeader>

                    <DialogBody className="px-5 pt-4">
                        <ul className="space-y-2">
                            {shortcuts.map((shortcut) => {
                                const Icon = shortcut.icon;
                                return (
                                    <li key={shortcut.title}>
                                        <button
                                            type="button"
                                            onClick={shortcut.onSelect}
                                            className="w-full text-left p-3.5 rounded-xl border border-line bg-surface hover:bg-surface-hover hover:border-line-strong transition-colors focus-ring"
                                        >
                                            <span className="flex items-center gap-3">
                                                <IconTile tone={shortcut.tone} size="md">
                                                    <Icon strokeWidth={2} />
                                                </IconTile>
                                                <span className="min-w-0">
                                                    <span className="block text-ui-lg font-semibold text-content">
                                                        {shortcut.title}
                                                    </span>
                                                    <span className="block text-meta text-content-muted mt-0.5 leading-relaxed">
                                                        {shortcut.description}
                                                    </span>
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>

                        <form onSubmit={submitPrompt} className="mt-5 pt-4 border-t border-line-subtle">
                            <Field label="Or ask a question">
                                {(control) => (
                                    <Input
                                        {...control}
                                        value={assistantPrompt}
                                        onChange={(e) => setAssistantPrompt(e.target.value)}
                                        placeholder="How did last month's campaigns perform?"
                                    />
                                )}
                            </Field>
                            <Button type="submit" fullWidth className="mt-3">
                                Open AI Assistant
                            </Button>
                        </form>
                    </DialogBody>
                </DialogContent>
            </Dialog>
        );
    };

    const handleSaveSegment = (segmentData) => {
        // Calculate real-time segment size using current leads data
        const refreshedSegmentData = {
            ...segmentData,
            size: calculateSegmentSize(segmentData.criteria, leads),
            updatedAt: new Date(),
        };

        if (editingSegment) {
            // Update existing segment with refreshed membership
            setSegments(
                segments.map((segment) =>
                    segment.id === segmentData.id
                        ? refreshedSegmentData
                        : segment,
                ),
            );
        } else {
            // Add new segment with initial membership calculation
            setSegments([...segments, refreshedSegmentData]);
        }
    };

    // Helper function to calculate segment size
    const calculateSegmentSize = (criteria, leadsData) => {
        if (!criteria || criteria.length === 0) return 0;

        try {
            // Import the segment evaluation utility
            const {
                filterLeadsByCriteria,
            } = require("./utils/segmentEvaluation");
            const matchingLeads = filterLeadsByCriteria(leadsData, criteria);
            return matchingLeads.length;
        } catch (error) {
            console.error("Error calculating segment size:", error);
            return 0;
        }
    };

    // Enhanced segment management functions
    const handleDeleteSegment = async (segmentId) => {
        // Was window.confirm(), which renders OS chrome that ignores the theme
        // and can only ask "are you sure?" — it could not name the segment or
        // say how many contacts it covers.
        const segment = segments.find((s) => s.id === segmentId);
        const ok = await confirm({
            title: segment ? `Delete "${segment.name}"?` : "Delete this segment?",
            description: segment?.size
                ? `This segment targets ${Number(segment.size).toLocaleString()} contacts. Deleting it won't remove the contacts themselves, but any campaign using it will lose its audience.`
                : "Deleting a segment won't remove the contacts in it, but any campaign using it will lose its audience.",
            confirmLabel: "Delete segment",
            tone: "danger",
        });
        if (!ok) return;
        setSegments(segments.filter((segment) => segment.id !== segmentId));
        toast.success({ title: "Segment deleted" });
    };

    const handleDuplicateSegment = (segment) => {
        const duplicatedSegment = {
            ...segment,
            id: Date.now().toString(),
            name: `${segment.name} (Copy)`,
            createdAt: new Date(),
            updatedAt: new Date(),
            usageCount: 0,
            lastUsed: null,
            size: calculateSegmentSize(segment.criteria, leads),
        };
        setSegments([...segments, duplicatedSegment]);
    };

    const AudiencesView = () => {
        const handleViewSourceLeads = (segment) => {
            setSegmentFilterFromAudiences(segment.id);
            setSuccessMessage(`Viewing ${segment.name} leads`);
            setShowSuccessNotification(true);
            setActiveTab("leads");
            setTimeout(() => {
                setShowSuccessNotification(false);
            }, 3000);
        };

        return (
            <EnhancedAudiencesView
                segments={segments}
                onCreateSegment={() => setShowCreateSegmentModal(true)}
                onEditSegment={(segment) => {
                    setEditingSegment(segment);
                    setShowCreateSegmentModal(true);
                }}
                onDeleteSegment={handleDeleteSegment}
                onDuplicateSegment={handleDuplicateSegment}
                onViewSourceLeads={handleViewSourceLeads}
                leads={leads}
            />
        );
    };

    const LeadsView = () => {
        // Handle create segment from selection
        const handleCreateSegmentFromSelection = (selectedLeadIds) => {
            return new Promise((resolve, reject) => {
                try {
                    // Handle empty selection edge case
                    if (!selectedLeadIds || selectedLeadIds.length === 0) {
                        const error = new Error(
                            "No leads selected for segment creation",
                        );
                        console.warn(error.message);
                        reject(error);
                        return;
                    }

                    // Get the selected lead data
                    const selectedLeadData = leads.filter((lead) =>
                        selectedLeadIds.includes(lead.id),
                    );

                    // Validate that we found the leads
                    if (selectedLeadData.length === 0) {
                        const error = new Error("Selected leads not found");
                        console.error(error.message);
                        reject(error);
                        return;
                    }

                    // Generate a new segment ID
                    const newSegmentId = (
                        Math.max(...segments.map((s) => parseInt(s.id))) + 1
                    ).toString();

                    // Create a new segment based on selected leads
                    // Create criteria for each selected lead ID with OR logic
                    const criteria = selectedLeadIds.map((leadId, index) => ({
                        id: (index + 1).toString(),
                        field: "id",
                        operator: "equals",
                        value: leadId,
                        weight: 1,
                        logicalOperator: index === 0 ? undefined : "OR", // First criterion has no logical operator, rest use OR
                    }));

                    const newSegment = {
                        id: newSegmentId,
                        name: `Custom Segment ${new Date().toLocaleDateString()}`,
                        description: `Segment created from ${selectedLeadData.length} selected leads`,
                        size: selectedLeadData.length,
                        count: selectedLeadData.length,
                        growth: "+0%",
                        color: "blue",
                        aiGenerated: false,
                        createdBy: "user",
                        criteria: criteria,
                        status: "active",
                        tags: ["custom", "user-created"],
                        createdAt: new Date(),
                        updatedAt: new Date(),
                        usageCount: 0,
                        lastUsed: new Date(),
                        expectedPerformance: {
                            openRate: 0.25,
                            clickRate: 0.05,
                            conversionRate: 0.03,
                        },
                        campaignResults: [],
                    };

                    // Add the new segment to the segments array
                    setSegments([...segments, newSegment]);

                    // Show success notification
                    setSuccessMessage(
                        `Created segment "${newSegment.name}" with ${selectedLeadData.length} leads`,
                    );
                    setShowSuccessNotification(true);
                    setTimeout(() => setShowSuccessNotification(false), 5000);

                    // Resolve the promise to indicate success
                    resolve(newSegment);
                } catch (error) {
                    console.error("Failed to create segment:", error);
                    reject(error);
                }
            });
        };

        // Handle add leads to existing segment
        const handleAddToSegment = (selectedLeadIds, segmentId) => {
            return new Promise((resolve, reject) => {
                try {
                    // Handle empty selection edge case
                    if (!selectedLeadIds || selectedLeadIds.length === 0) {
                        const error = new Error(
                            "No leads selected to add to segment",
                        );
                        console.warn(error.message);
                        reject(error);
                        return;
                    }

                    // Validate that segments exist
                    if (segments.length === 0) {
                        const error = new Error(
                            "No segments available to add leads to",
                        );
                        console.error(error.message);
                        reject(error);
                        return;
                    }

                    // Find the target segment
                    const targetSegment = segments.find(
                        (segment) => segment.id === segmentId,
                    );
                    if (!targetSegment) {
                        const error = new Error("Target segment not found");
                        console.error(error.message);
                        reject(error);
                        return;
                    }

                    // Update segment to include manually added leads
                    const updatedSegments = segments.map((segment) => {
                        if (segment.id === targetSegment.id) {
                            // Get existing manual lead IDs or initialize empty array
                            const existingManualLeadIds =
                                segment.manualLeadIds || [];

                            // Add new lead IDs, avoiding duplicates
                            const newManualLeadIds = [
                                ...new Set([
                                    ...existingManualLeadIds,
                                    ...selectedLeadIds,
                                ]),
                            ];

                            // Calculate how many new leads were actually added (not duplicates)
                            const newLeadsCount =
                                newManualLeadIds.length -
                                existingManualLeadIds.length;

                            return {
                                ...segment,
                                manualLeadIds: newManualLeadIds,
                                size: (segment.size || 0) + newLeadsCount,
                                count: (segment.count || 0) + newLeadsCount,
                                updatedAt: new Date(),
                                usageCount: (segment.usageCount || 0) + 1,
                                lastUsed: new Date(),
                            };
                        }
                        return segment;
                    });

                    setSegments(updatedSegments);

                    // Show success notification
                    setSuccessMessage(
                        `Added ${selectedLeadIds.length} lead${selectedLeadIds.length !== 1 ? "s" : ""} to "${targetSegment.name}" segment`,
                    );
                    setShowSuccessNotification(true);
                    setTimeout(() => setShowSuccessNotification(false), 5000);

                    // Resolve the promise to indicate success
                    resolve(targetSegment);
                } catch (error) {
                    console.error("Failed to add leads to segment:", error);
                    reject(error);
                }
            });
        };

        // Handle return to audiences
        const handleReturnToAudiences = () => {
            setActiveTab("audiences");
            setSuccessMessage("Returned to Audiences view");
            setShowSuccessNotification(true);
            setTimeout(() => {
                setShowSuccessNotification(false);
            }, 3000);
        };

        // Handle edit lead - integrates with existing lead editing functionality
        const handleEditLead = (lead) => {
            try {
                console.log("Edit lead:", lead);

                // Validate lead data
                if (!lead || !lead.id) {
                    throw new Error("Invalid lead data provided for editing");
                }

                // Find the lead in the current leads array
                const existingLead = leads.find((l) => l.id === lead.id);
                if (!existingLead) {
                    throw new Error(`Lead with ID ${lead.id} not found`);
                }

                // In a real implementation, this would open an edit modal or form
                // For now, we'll simulate the edit functionality by showing a success message
                // and logging the lead data that would be edited

                setSuccessMessage(
                    `Edit mode activated for ${lead.name}. Lead editing interface would open here.`,
                );
                setShowSuccessNotification(true);
                setTimeout(() => setShowSuccessNotification(false), 4000);

                // Log lead details for debugging
                console.log("Lead details for editing:", {
                    id: lead.id,
                    name: lead.name,
                    email: lead.email,
                    status: lead.status,
                    leadScore: lead.leadScore,
                    source: lead.source,
                    location: lead.location,
                    phone: lead.phone,
                    lastEngaged: lead.lastEngaged,
                    tags: lead.tags,
                });
            } catch (error) {
                console.error("Failed to edit lead:", error);
                setSuccessMessage(`Error: ${error.message}`);
                setShowSuccessNotification(true);
                setTimeout(() => setShowSuccessNotification(false), 4000);
            }
        };

        // Handle delete lead - removes lead from leads array
        const handleDeleteLead = (leadId) => {
            try {
                console.log("Delete lead:", leadId);

                // Validate lead ID
                if (!leadId) {
                    throw new Error("Lead ID is required for deletion");
                }

                // Find the lead to be deleted
                const leadToDelete = leads.find((lead) => lead.id === leadId);
                if (!leadToDelete) {
                    throw new Error(`Lead with ID ${leadId} not found`);
                }

                // Remove the lead from the leads array
                const updatedLeads = leads.filter((lead) => lead.id !== leadId);
                setLeads(updatedLeads);

                // Update segments that might contain this lead manually
                const updatedSegments = segments.map((segment) => {
                    if (
                        segment.manualLeadIds &&
                        segment.manualLeadIds.includes(leadId)
                    ) {
                        const newManualLeadIds = segment.manualLeadIds.filter(
                            (id) => id !== leadId,
                        );
                        return {
                            ...segment,
                            manualLeadIds: newManualLeadIds,
                            size: Math.max(0, (segment.size || 0) - 1),
                            count: Math.max(0, (segment.count || 0) - 1),
                            updatedAt: new Date(),
                        };
                    }
                    return segment;
                });
                setSegments(updatedSegments);

                // Show success notification
                setSuccessMessage(
                    `Successfully deleted ${leadToDelete.name} from leads database`,
                );
                setShowSuccessNotification(true);
                setTimeout(() => setShowSuccessNotification(false), 4000);

                console.log(
                    `Lead ${leadToDelete.name} (ID: ${leadId}) successfully deleted`,
                );
            } catch (error) {
                console.error("Failed to delete lead:", error);
                setSuccessMessage(`Error deleting lead: ${error.message}`);
                setShowSuccessNotification(true);
                setTimeout(() => setShowSuccessNotification(false), 4000);
            }
        };

        // Handle email lead - integrates with email functionality
        const handleEmailLead = (lead) => {
            try {
                console.log("Email lead:", lead);

                // Validate lead data
                if (!lead || !lead.email) {
                    throw new Error("Lead email address is required");
                }

                // Validate email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(lead.email)) {
                    throw new Error("Invalid email address format");
                }

                // In a real implementation, this would:
                // 1. Open an email composer modal
                // 2. Pre-populate recipient with lead's email
                // 3. Suggest email templates based on lead status/segment
                // 4. Track email engagement

                // For now, we'll simulate the email functionality
                const emailData = {
                    to: lead.email,
                    subject: `Follow-up with ${lead.name}`,
                    leadInfo: {
                        name: lead.name,
                        status: lead.status,
                        leadScore: lead.leadScore,
                        lastEngaged: lead.lastEngaged,
                        source: lead.source,
                    },
                };

                // Update lead's last engaged date to current date
                const updatedLeads = leads.map((l) =>
                    l.id === lead.id
                        ? {
                            ...l,
                            lastEngaged: new Date()
                                .toISOString()
                                .split("T")[0],
                        }
                        : l,
                );
                setLeads(updatedLeads);

                // Show success notification
                setSuccessMessage(
                    `Email composer opened for ${lead.name} (${lead.email}). Email tracking activated.`,
                );
                setShowSuccessNotification(true);
                setTimeout(() => setShowSuccessNotification(false), 4000);

                // Log email details for debugging
                console.log("Email composition data:", emailData);
            } catch (error) {
                console.error("Failed to email lead:", error);
                setSuccessMessage(`Error sending email: ${error.message}`);
                setShowSuccessNotification(true);
                setTimeout(() => setShowSuccessNotification(false), 4000);
            }
        };

        // Handle segment filter change
        const handleSegmentFilterChange = (segmentId) => {
            setSegmentFilterFromAudiences(segmentId);
        };

        // Handle add lead - opens the Add Lead modal
        return (
            <EnhancedLeadsView
                leads={leads}
                segments={segments}
                onCreateSegmentFromSelection={handleCreateSegmentFromSelection}
                onAddToSegment={handleAddToSegment}
                onEditLead={handleEditLead}
                onDeleteLead={handleDeleteLead}
                onEmailLead={handleEmailLead}
                selectedSegmentFilter={segmentFilterFromAudiences || "all"}
                onSegmentFilterChange={handleSegmentFilterChange}
                onReturnToAudiences={handleReturnToAudiences}
            />
        );
    };

    const ABDemoView = () => <ABTestingDemo />;

    // Live A/B Monitoring View (moved out of demo)
    const LiveMonitoringView = () => (
        <Page>
            <PageHeader
                icon={<Eye className="w-5 h-5" />}
                tone="info"
                title="Live A/B Test Monitoring"
                subtitle="Real-time monitoring and automated winner detection for your A/B tests."
            />
            <LiveMonitoringDemo />
        </Page>
    );

    // AI Agent Transparency Views
    const AIAgentsMainView = () => (
        <Page>
            <AIAgentDashboard />
        </Page>
    );

    const AgentCollaborationView = () => (
        <Page>
            <AgentCollaborationDashboard
                agents={[]}
                collaborations={[]}
                communications={[]}
                onCollaborationCreate={() => { }}
                onCommunicationSend={() => { }}
                onConflictResolve={() => { }}
            />
        </Page>
    );

    const DecisionExplainerView = () => {
        // Mock decision data for demonstration
        const mockDecision = {
            id: "decision_001",
            type: "test_creation",
            timestamp: new Date(),
            context: { templateId: "template_123", testId: "test_456" },
            reasoning:
                "Based on historical performance data and current market trends, creating an A/B test for the email subject line will likely improve open rates by 15-20%.",
            confidence: 0.87,
            riskLevel: "medium",
            alternatives: [
                {
                    id: "alt_1",
                    description:
                        "Continue with current subject line without testing",
                    confidence: 0.45,
                    expectedOutcome: { openRate: 0.22, clickRate: 0.03 },
                    pros: [
                        "No additional resources required",
                        "Maintains consistency",
                    ],
                    cons: [
                        "Missed optimization opportunity",
                        "Lower performance potential",
                    ],
                    riskLevel: "low",
                },
                {
                    id: "alt_2",
                    description: "Implement multivariate testing instead",
                    confidence: 0.72,
                    expectedOutcome: { openRate: 0.28, clickRate: 0.045 },
                    pros: [
                        "More comprehensive insights",
                        "Tests multiple variables",
                    ],
                    cons: [
                        "Requires larger sample size",
                        "More complex analysis",
                    ],
                    riskLevel: "high",
                },
            ],
            approvalStatus: "pending",
            explanation: {
                summary:
                    "Recommending A/B test creation for email subject line optimization based on performance analysis.",
                details:
                    "Analysis of the current email campaign shows that subject line performance is below industry benchmarks. Historical data indicates that similar A/B tests have resulted in 15-20% improvement in open rates. The proposed test will compare the current subject line against 2 AI-generated alternatives, with statistical significance expected within 7-14 days.",
                dataPoints: [
                    {
                        metric: "Current Open Rate",
                        value: "22%",
                        benchmark: "28%",
                    },
                    {
                        metric: "Historical A/B Test Success",
                        value: "78%",
                        sample: "45 tests",
                    },
                    {
                        metric: "Expected Improvement",
                        value: "15-20%",
                        confidence: "87%",
                    },
                ],
                assumptions: [
                    "Audience behavior remains consistent with historical patterns",
                    "No major external factors affecting email performance",
                    "Subject line is the primary factor affecting open rates",
                ],
            },
        };

        const mockAgent = {
            id: "agent_001",
            name: "Email Optimization Agent",
            specialization: "ab_test_optimizer",
            status: "active",
            automationLevel: "supervised",
            configuration: {
                riskTolerance: "medium",
                approvalThresholds: {},
                performanceTargets: {
                    successRate: 0.8,
                    improvementRate: 0.15,
                    responseTime: 30,
                },
                constraints: {
                    maxConcurrentTasks: 5,
                    maxBudgetPerTest: 1000,
                    allowedTestDuration: { min: 7, max: 30 },
                    restrictedElements: [],
                },
                preferences: {
                    preferredTestTypes: ["subject_line", "content"],
                    defaultConfidenceLevel: 85,
                    communicationStyle: "summary",
                },
            },
            performance: {
                overall: {
                    successRate: 0.85,
                    averageConfidence: 0.82,
                    tasksCompleted: 156,
                    averageTaskDuration: 45,
                    improvementRate: 0.18,
                },
                byTaskType: {},
                trends: { period: "weekly", data: [] },
                benchmarks: {
                    vsOtherAgents: 85,
                    vsHistorical: 0.12,
                    vsTargets: {},
                },
                feedback: [],
            },
            capabilities: [],
            currentTasks: [],
            collaborations: [],
            learningData: {
                knowledgeBase: {
                    patterns: [],
                    bestPractices: [],
                    failures: [],
                    insights: [],
                },
                adaptations: {
                    behaviorChanges: [],
                    capabilityImprovements: [],
                    strategyRefinements: [],
                },
                trainingData: {
                    lastTrainingDate: new Date(),
                    trainingDataSize: 1000,
                    modelVersion: "1.0",
                    accuracyMetrics: {},
                },
            },
            metadata: {
                createdAt: new Date(),
                lastActive: new Date(),
                version: "1.0",
                description: "Specialized in email A/B testing optimization",
                tags: ["email", "optimization"],
            },
        };

        return (
            <Page>
                <PageHeader
                    icon={<Target className="w-5 h-5" />}
                    tone="violet"
                    title="AI Agent Decision Explainer"
                    subtitle="Understand how AI agents make decisions with detailed explanations and transparency."
                />
                <AIAgentDecisionExplainer
                    decision={mockDecision}
                    agent={mockAgent}
                    onApprove={(decisionId, feedback) =>
                        console.log("Approved:", decisionId, feedback)
                    }
                    onReject={(decisionId, reason) =>
                        console.log("Rejected:", decisionId, reason)
                    }
                    onRequestMoreInfo={(decisionId, questions) =>
                        console.log(
                            "More info requested:",
                            decisionId,
                            questions,
                        )
                    }
                />
            </Page>
        );
    };

    const ReasoningTrailsView = () => {
        // Mock decision and agent data
        const mockDecision = {
            id: "decision_002",
            type: "winner_implementation",
            timestamp: new Date(),
            context: { testId: "test_789", winningVariant: "variant_b" },
            reasoning:
                "Variant B achieved statistical significance with 95% confidence after 14 days of testing.",
            confidence: 0.95,
            riskLevel: "low",
            alternatives: [],
            approvalStatus: "approved",
            explanation: {
                summary:
                    "Implementing winning variant B based on statistical analysis.",
                details:
                    "Test results show variant B outperformed the control with 23% higher click-through rate.",
                dataPoints: [],
                assumptions: [],
            },
        };

        const mockAgent = {
            id: "agent_002",
            name: "Statistical Analysis Agent",
            specialization: "statistical_analyst",
            status: "active",
            automationLevel: "autonomous",
            configuration: {
                riskTolerance: "low",
                approvalThresholds: {},
                performanceTargets: {
                    successRate: 0.9,
                    improvementRate: 0.12,
                    responseTime: 20,
                },
                constraints: {
                    maxConcurrentTasks: 3,
                    maxBudgetPerTest: 5000,
                    allowedTestDuration: { min: 14, max: 60 },
                    restrictedElements: [],
                },
                preferences: {
                    preferredTestTypes: ["statistical_analysis"],
                    defaultConfidenceLevel: 95,
                    communicationStyle: "detailed",
                },
            },
            performance: {
                overall: {
                    successRate: 0.92,
                    averageConfidence: 0.89,
                    tasksCompleted: 89,
                    averageTaskDuration: 25,
                    improvementRate: 0.14,
                },
                byTaskType: {},
                trends: { period: "weekly", data: [] },
                benchmarks: {
                    vsOtherAgents: 92,
                    vsHistorical: 0.08,
                    vsTargets: {},
                },
                feedback: [],
            },
            capabilities: [],
            currentTasks: [],
            collaborations: [],
            learningData: {
                knowledgeBase: {
                    patterns: [],
                    bestPractices: [],
                    failures: [],
                    insights: [],
                },
                adaptations: {
                    behaviorChanges: [],
                    capabilityImprovements: [],
                    strategyRefinements: [],
                },
                trainingData: {
                    lastTrainingDate: new Date(),
                    trainingDataSize: 2000,
                    modelVersion: "1.2",
                    accuracyMetrics: {},
                },
            },
            metadata: {
                createdAt: new Date(),
                lastActive: new Date(),
                version: "1.2",
                description:
                    "Expert in statistical analysis and A/B test evaluation",
                tags: ["statistics", "analysis"],
            },
        };

        return (
            <Page>
                <PageHeader
                    icon={<Activity className="w-5 h-5" />}
                    tone="violet"
                    title="Agent Reasoning Trails"
                    subtitle="Follow the step-by-step reasoning process of AI agents as they make decisions."
                />
                <AgentReasoningTrail
                    decision={mockDecision}
                    agent={mockAgent}
                    onStepSelect={(step) => console.log("Step selected:", step)}
                />
            </Page>
        );
    };

    const ImpactTrackerView = () => {
        const mockAgent = {
            id: "agent_003",
            name: "Performance Optimization Agent",
            specialization: "performance_analyzer",
            status: "active",
            automationLevel: "supervised",
            configuration: {
                riskTolerance: "medium",
                approvalThresholds: {},
                performanceTargets: {
                    successRate: 0.85,
                    improvementRate: 0.2,
                    responseTime: 35,
                },
                constraints: {
                    maxConcurrentTasks: 8,
                    maxBudgetPerTest: 2000,
                    allowedTestDuration: { min: 7, max: 45 },
                    restrictedElements: [],
                },
                preferences: {
                    preferredTestTypes: ["performance", "optimization"],
                    defaultConfidenceLevel: 90,
                    communicationStyle: "summary",
                },
            },
            performance: {
                overall: {
                    successRate: 0.88,
                    averageConfidence: 0.85,
                    tasksCompleted: 234,
                    averageTaskDuration: 32,
                    improvementRate: 0.22,
                },
                byTaskType: {
                    test_creation: {
                        successRate: 0.85,
                        averageConfidence: 0.82,
                        count: 45,
                        averageDuration: 28,
                    },
                    winner_implementation: {
                        successRate: 0.92,
                        averageConfidence: 0.89,
                        count: 38,
                        averageDuration: 15,
                    },
                    campaign_optimization: {
                        successRate: 0.87,
                        averageConfidence: 0.84,
                        count: 52,
                        averageDuration: 42,
                    },
                },
                trends: { period: "weekly", data: [] },
                benchmarks: {
                    vsOtherAgents: 88,
                    vsHistorical: 0.15,
                    vsTargets: {},
                },
                feedback: [],
            },
            capabilities: [],
            currentTasks: [],
            collaborations: [],
            learningData: {
                knowledgeBase: {
                    patterns: [],
                    bestPractices: [],
                    failures: [],
                    insights: [],
                },
                adaptations: {
                    behaviorChanges: [],
                    capabilityImprovements: [],
                    strategyRefinements: [],
                },
                trainingData: {
                    lastTrainingDate: new Date(),
                    trainingDataSize: 3000,
                    modelVersion: "1.1",
                    accuracyMetrics: {},
                },
            },
            metadata: {
                createdAt: new Date(),
                lastActive: new Date(),
                version: "1.1",
                description:
                    "Focuses on performance optimization and impact measurement",
                tags: ["performance", "optimization", "analytics"],
            },
        };

        return (
            <Page>
                <PageHeader
                    icon={<TrendingUp className="w-5 h-5" />}
                    tone="success"
                    title="AI Agent Impact Tracker"
                    subtitle="Track the business impact and performance attribution of AI agent decisions."
                />
                <AIAgentImpactTracker
                    agent={mockAgent}
                    timeRange="30d"
                    onMetricSelect={(metric) =>
                        console.log("Metric selected:", metric)
                    }
                    onImpactSelect={(impact) =>
                        console.log("Impact selected:", impact)
                    }
                />
            </Page>
        );
    };

    const LearningProgressionView = () => {
        const mockAgent = {
            id: "agent_004",
            name: "Learning & Adaptation Agent",
            specialization: "content_optimizer",
            status: "learning",
            automationLevel: "assisted",
            configuration: {
                riskTolerance: "high",
                approvalThresholds: {},
                performanceTargets: {
                    successRate: 0.75,
                    improvementRate: 0.25,
                    responseTime: 40,
                },
                constraints: {
                    maxConcurrentTasks: 6,
                    maxBudgetPerTest: 1500,
                    allowedTestDuration: { min: 10, max: 30 },
                    restrictedElements: [],
                },
                preferences: {
                    preferredTestTypes: ["content", "creative"],
                    defaultConfidenceLevel: 80,
                    communicationStyle: "detailed",
                },
            },
            performance: {
                overall: {
                    successRate: 0.78,
                    averageConfidence: 0.76,
                    tasksCompleted: 167,
                    averageTaskDuration: 38,
                    improvementRate: 0.28,
                },
                byTaskType: {
                    content_modification: {
                        successRate: 0.82,
                        averageConfidence: 0.79,
                        count: 67,
                        averageDuration: 35,
                    },
                    variant_generation: {
                        successRate: 0.75,
                        averageConfidence: 0.73,
                        count: 54,
                        averageDuration: 42,
                    },
                },
                trends: { period: "weekly", data: [] },
                benchmarks: {
                    vsOtherAgents: 78,
                    vsHistorical: 0.18,
                    vsTargets: {},
                },
                feedback: [],
            },
            capabilities: [
                {
                    id: "cap_1",
                    name: "Content Analysis",
                    type: "core",
                    proficiency: 85,
                    description: "Analyze content effectiveness",
                    prerequisites: [],
                    usageCount: 145,
                    successRate: 0.82,
                },
                {
                    id: "cap_2",
                    name: "Creative Generation",
                    type: "specialized",
                    proficiency: 72,
                    description: "Generate creative content variations",
                    prerequisites: ["cap_1"],
                    usageCount: 89,
                    successRate: 0.75,
                },
                {
                    id: "cap_3",
                    name: "Sentiment Analysis",
                    type: "learned",
                    proficiency: 68,
                    description: "Analyze content sentiment and tone",
                    prerequisites: [],
                    usageCount: 67,
                    successRate: 0.78,
                },
            ],
            currentTasks: [],
            collaborations: [],
            learningData: {
                knowledgeBase: {
                    patterns: [
                        {
                            id: "pattern_1",
                            pattern:
                                "Emotional subject lines increase open rates",
                            context: ["email", "subject_lines"],
                            frequency: 23,
                            successRate: 0.84,
                            confidence: 0.89,
                            firstObserved: new Date(
                                Date.now() - 60 * 24 * 60 * 60 * 1000,
                            ),
                            lastObserved: new Date(),
                            applications: [
                                "Summer Sale Campaign",
                                "Welcome Series",
                                "Re-engagement Campaign",
                            ],
                        },
                    ],
                    bestPractices: [
                        {
                            id: "bp_1",
                            title: "Use urgency in promotional emails",
                            description:
                                "Adding time-sensitive language increases conversion rates",
                            category: "content",
                            effectiveness: 0.87,
                            applicableContexts: ["promotional", "sales"],
                            learnedFrom: ["test_123", "test_456"],
                            validatedCount: 15,
                            lastValidated: new Date(),
                        },
                    ],
                    failures: [],
                    insights: [
                        {
                            id: "insight_1",
                            type: "user_behavior",
                            title: "Mobile users prefer shorter subject lines",
                            description:
                                "Analysis shows mobile users have 23% higher open rates with subject lines under 30 characters",
                            confidence: 0.82,
                            supportingData: [],
                            discoveredAt: new Date(),
                            actionable: true,
                            recommendations: [
                                "Keep mobile subject lines under 30 characters",
                                "Test mobile-specific variants",
                            ],
                            impact: "medium",
                        },
                    ],
                },
                adaptations: {
                    behaviorChanges: [
                        {
                            id: "bc_1",
                            trigger: "Low performance on creative content",
                            oldBehavior: "Generate single creative variant",
                            newBehavior:
                                "Generate multiple creative variants and test",
                            reason: "Improve success rate through variety",
                            implementedAt: new Date(
                                Date.now() - 14 * 24 * 60 * 60 * 1000,
                            ),
                            effectiveness: 0.15,
                        },
                    ],
                    capabilityImprovements: [
                        {
                            capabilityId: "cap_2",
                            improvementType: "accuracy",
                            oldLevel: 65,
                            newLevel: 72,
                            achievedAt: new Date(
                                Date.now() - 7 * 24 * 60 * 60 * 1000,
                            ),
                            method: "Additional training data",
                            validationResults: {
                                accuracy: 0.72,
                                precision: 0.68,
                                recall: 0.75,
                            },
                        },
                    ],
                    strategyRefinements: [],
                },
                trainingData: {
                    lastTrainingDate: new Date(
                        Date.now() - 3 * 24 * 60 * 60 * 1000,
                    ),
                    trainingDataSize: 1800,
                    modelVersion: "1.3",
                    accuracyMetrics: {
                        overall: 0.76,
                        content: 0.78,
                        creative: 0.72,
                    },
                },
            },
            metadata: {
                createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
                lastActive: new Date(),
                version: "1.3",
                description:
                    "Specializes in content optimization and creative generation with continuous learning",
                tags: ["content", "creative", "learning"],
            },
        };

        return (
            <Page>
                <PageHeader
                    icon={<Sparkles className="w-5 h-5" />}
                    tone="violet"
                    title="Agent Learning Progression"
                    subtitle="Monitor AI agent learning milestones, capability evolution, and knowledge acquisition."
                />
                <AgentLearningProgression
                    agent={mockAgent}
                    timeRange="30d"
                    onMilestoneSelect={(milestone) =>
                        console.log("Milestone selected:", milestone)
                    }
                    onCapabilitySelect={(capability) =>
                        console.log("Capability selected:", capability)
                    }
                />
            </Page>
        );
    };

    const renderContent = () => {
        return (
            <Routes>
                <Route
                    path="/dashboard"
                    element={
                        <EnhancedDashboard
                            onShowAIAssistant={() =>
                                setShowAIAssistant(!showAIAssistant)
                            }
                            onShowNewCampaign={() =>
                                setShowNewCampaignModal(true)
                            }
                            showAIAssistant={showAIAssistant}
                            notifications={[]} // Add real notifications later
                        />
                    }
                />
                <Route
                    path="/campaigns"
                    element={
                        <EnhancedCampaignsView
                            /* The list itself comes from `useCampaigns` inside
                               EnhancedCampaignsView. This prop only feeds the
                               dev-only "Type" filter, and it used to carry
                               fixtures — so a real account saw invented
                               campaigns mixed into its own data. */
                            campaigns={[]}
                            segments={[]} // Pass real segments here
                            /* No agents endpoint exists; an empty list is the
                               honest answer, and the picker renders its empty
                               state rather than offering agents that are not
                               real. */
                            aiAgents={[]}
                            templates={templates}
                            onCreateCampaign={handleCreateCampaignFromView}
                            onEditCampaign={handleEditCampaign}
                            onDeleteCampaign={handleDeleteCampaign}
                            onDuplicateCampaign={handleDuplicateCampaign}
                            onPauseCampaign={handlePauseCampaign}
                            onResumeCampaign={handleResumeCampaign}
                            onViewDetails={handleViewCampaignDetails}
                            onConfigureAI={handleConfigureAI}
                        />
                    }
                />
                <Route path="/campaigns/new" element={<CampaignForm />} />
                <Route path="/campaigns/flow" element={<DrawflowComponent />} />
                <Route path="/ab-testing" element={<ABTestingPage />} />
                <Route path="/identity" element={<IdentityComponent />} />
                <Route path="/widgets" element={<Widgets />} />

                <Route
                    path="/live-monitoring"
                    element={<LiveMonitoringView />}
                />
                <Route path="/ai-agents" element={<AIAgentsMainView />} />
                <Route
                    path="/agent-collaboration"
                    element={<AgentCollaborationView />}
                />
                <Route
                    path="/decision-explainer"
                    element={<DecisionExplainerView />}
                />
                <Route
                    path="/reasoning-trails"
                    element={<ReasoningTrailsView />}
                />
                <Route path="/impact-tracker" element={<ImpactTrackerView />} />
                <Route
                    path="/learning-progression"
                    element={<LearningProgressionView />}
                />
                <Route path="/audiences" element={<AudiencesView />} />
                <Route path="/leads" element={<LeadsView />} />
                <Route path="/leads/new" element={<LeadsForm />} />
                <Route path="/leads/:group/:groupName/new" element={<LeadsForm />} />
                <Route path="/leads/:group/:groupName/:id" element={<LeadsForm />} />
                <Route path="/leads/manage-groups/:id/:name" element={<LeadsGroupForm />} />
                <Route path="/trash" element={<Trash />} />
                {/* Added New Leads Form Routes */}

                <Route path="/launch" element={<Launch />} />

                <Route path="/integrations" element={<APIIntegrationsView />} />
                {/* Merko Integrations: the trusted page a provider redirects back
                    to. It completes a connection attempt, and sends the user back
                    to /integrations when the feature is not enabled here. */}
                <Route path={INTEGRATIONS_CALLBACK_PATH} element={<IntegrationsCallbackRoute />} />
                <Route path="/history" element={<HistoryView />} />

                {/* Template Editor */}
                <Route path="/templates" element={<TemplatesView />} />
                <Route path="/templates/editor/:templateId" element={<TemplateEditor />} />

                {/* Content Builder */}
                <Route path="/content-builder" element={<ContentBuilderPage />} />
                <Route path="/template-list" element={<TemplateListPage />} />

                <Route path="/support" element={<SupportDashboard />} />
                {/* Billing & Plans — the single money surface. `/billing` and
                    `/transactions-history` below redirect here; both were
                    separate implementations of these same three tabs. */}
                <Route path="/payments" element={<PaymentsView />} />
                <Route path="/feedback" element={<FeedbackView />} />
                <Route path="/help" element={<HelpView />} />
                <Route path="/document" element={<DocumentView />} />
                <Route
                    path="/transactions-history"
                    element={<Navigate to="/payments?tab=transactions" replace />}
                />
                <Route path="/demo" element={<ABDemoView />} />
                <Route path="/agent" element={<AiAgent />} />
                <Route path="/ai-playground" element={<AIPlayground />} />

                {/* Navbar Options */}
                <Route path="/developer" element={<DeveloperSetting />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/billing" element={<Navigate to="/payments" replace />} />

                {/* Default fallback */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        );
    };

    return (
        <>
            {/* The shell — sidebar, header, scroll region — now lives in
                new-components/layout/AppShell so it can be reasoned about
                independently of this file's page logic. Scroll containment,
                iOS safe areas and full-bleed route handling moved with it,
                unchanged. */}
            <AppShell>{renderContent()}</AppShell>

            {newCampaignModalJSX}
            <CreateSegmentModal
                isOpen={showCreateSegmentModal}
                onClose={() => {
                    setShowCreateSegmentModal(false);
                    setEditingSegment(null);
                }}
                onSave={handleSaveSegment}
                leads={leads}
                existingSegments={segments}
                editingSegment={editingSegment}
            />
            <AIAssistant />
        </>
    );
};

const App = () => {
    const { data: profile, isLoading, isError } = useProfileDetails();

    // if (isLoading) {
    //     return <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-dark-700 text-sm text-gray-500">Loading your account…</div>;
    // }

    if (!isError && profile?.status === "DELETED") {
        return <ReactivationPage />;
    }

    return (

        <ThemeProvider>
            <NavigationProvider>
                <HeaderProvider>
                    <EmailMarketingDashboard />
                </HeaderProvider>
            </NavigationProvider>
        </ThemeProvider>
    );
};

export default App;
